/**
 * Pure helpers for the org-scoped skill editor.
 */

import type { SkillCatalogDetail } from "../contracts/skills.js";
import {
  SKILL_COMPANION_MAX_FILES,
  SKILL_DESC_MAX,
  SKILL_DESC_MIN,
  SKILL_MARKDOWN_MAX_BYTES,
  SKILL_TITLE_MAX,
  SKILL_TITLE_MIN,
  SKILL_TOTAL_MAX_BYTES,
} from "../contracts/skills.js";
import { decodedByteLength } from "./skill-files.js";

export interface SkillEditorCompanionFile {
  path: string;
  content: string;
  encoding: "utf8" | "base64";
}

export interface SkillEditorValues {
  title: string;
  description: string;
  markdown: string;
  companionFiles: SkillEditorCompanionFile[];
}

export interface SkillEditorErrors {
  title?: string;
  description?: string;
  markdown?: string;
  files?: string;
}

export {
  SKILL_COMPANION_MAX_FILES,
  SKILL_DESC_MAX,
  SKILL_DESC_MIN,
  SKILL_MARKDOWN_MAX_BYTES,
  SKILL_TITLE_MAX,
  SKILL_TITLE_MIN,
  SKILL_TOTAL_MAX_BYTES,
};

export function skillEditorInitialValues(
  skill?: Pick<SkillCatalogDetail, "title" | "description" | "markdown" | "companionFiles">,
): SkillEditorValues {
  return {
    title: skill?.title ?? "",
    description: skill?.description ?? "",
    markdown: skill?.markdown ?? "",
    companionFiles: skill?.companionFiles ? [...skill.companionFiles] : [],
  };
}

/** Drop a leading `---`-delimited frontmatter block from a SKILL.md body. */
export function stripFrontmatter(markdown: string): string {
  if (!markdown.startsWith("---\n")) return markdown;
  const end = markdown.indexOf("\n---", 4);
  if (end === -1) return markdown;
  const next = markdown.slice(end + 4);
  return next.startsWith("\n") ? next.slice(1) : next;
}

/**
 * The PATCH body for an existing skill: identity and `SKILL.md` only. Companion
 * structure is written immediately through the per-file ops, so an update never
 * carries `companionFiles` — that would corrupt the first binary companion the
 * moment the server re-read it as text. The body is stripped of frontmatter so
 * the wire never carries a `---\n…---` block (the server re-emits it).
 */
export function skillEditorUpdateValues(values: SkillEditorValues): {
  title: string;
  description: string;
  markdown: string;
} {
  return {
    title: values.title,
    description: values.description,
    markdown: stripFrontmatter(values.markdown),
  };
}

/**
 * The create body for a new skill: identity, `SKILL.md`, and the buffered
 * companion files (a new skill has no id yet, so its companions cannot be
 * written immediately and travel with the create request).
 */
export function skillEditorCreateValues(values: SkillEditorValues): {
  title: string;
  description: string;
  markdown: string;
  companionFiles: SkillEditorCompanionFile[];
} {
  return { ...skillEditorUpdateValues(values), companionFiles: values.companionFiles };
}

export function validateSkillEditorForm(values: SkillEditorValues): {
  valid: boolean;
  errors: SkillEditorErrors;
} {
  const errors: SkillEditorErrors = {};
  const t = (values.title ?? "").trim();
  if (t.length < SKILL_TITLE_MIN || t.length > SKILL_TITLE_MAX) {
    errors.title = `Title must be ${SKILL_TITLE_MIN}-${SKILL_TITLE_MAX} characters`;
  }
  const d = (values.description ?? "").trim();
  if (d.length < SKILL_DESC_MIN || d.length > SKILL_DESC_MAX) {
    errors.description = `Description must be ${SKILL_DESC_MIN}-${SKILL_DESC_MAX} characters`;
  }
  const markdown = values.markdown ?? "";
  if (!markdown.trim()) {
    errors.markdown = "SKILL.md cannot be empty";
  } else {
    const bytes = new TextEncoder().encode(markdown).byteLength;
    if (bytes > SKILL_MARKDOWN_MAX_BYTES) {
      errors.markdown = "SKILL.md exceeds 100 KB";
    } else if (/<script\b/i.test(markdown) || /<iframe\b/i.test(markdown)) {
      errors.markdown = "Script and iframe tags are not allowed";
    } else if (/\bjavascript:/i.test(markdown)) {
      errors.markdown = "javascript: URLs are not allowed";
    }
  }
  const files = values.companionFiles ?? [];
  if (files.length > SKILL_COMPANION_MAX_FILES) {
    errors.files = `Too many companion files (max ${SKILL_COMPANION_MAX_FILES})`;
  }
  let total = new TextEncoder().encode(markdown).byteLength;
  for (const f of files) {
    total += decodedByteLength(f.content ?? "", f.encoding ?? "utf8");
  }
  if (total > SKILL_TOTAL_MAX_BYTES) {
    errors.files = `Total skill size exceeds ${SKILL_TOTAL_MAX_BYTES / (1024 * 1024)} MB`;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}
